import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { useWeb3 } from '../context/Web3Context';
import { useLanguage } from '../context/LanguageContext';
import './PredictionSection.css';

const weiToUsd = (wei) => {
  if (!wei || wei === '0') return '0.00';
  try {
    const n = Number(BigInt(wei)) / 1e18;
    return n.toFixed(2);
  } catch { return '0.00'; }
};

const usdToWei = (usd) => {
  const s = String(usd || '').trim();
  if (!s) return '0';
  const [whole = '0', frac = ''] = s.split('.');
  const padded = (frac + '000000000000000000').slice(0, 18);
  const combined = BigInt(whole) * BigInt('1000000000000000000') + BigInt(padded);
  return combined.toString();
};

const PredictionSection = () => {
  const { t } = useLanguage();
  const { isConnected } = useWeb3();
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPred, setSelectedPred] = useState(null);
  const [myBets, setMyBets] = useState([]);
  const [betAmounts, setBetAmounts] = useState({});
  const [placing, setPlacing] = useState(null);

  const fetchPredictions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/predictions');
      setPredictions(res.data?.predictions || []);
    } catch (e) {
      console.error('Failed to load predictions', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPredictions(); }, [fetchPredictions]);

  const loadMyBets = async (predId) => {
    if (!isConnected) return;
    try {
      const res = await api.get(`/predictions/${predId}/my-bets`);
      setMyBets(res.data?.bets || []);
    } catch (_) { /* */ }
  };

  const handleSelect = (pred) => {
    const id = pred._id;
    setSelectedPred(selectedPred === id ? null : id);
    setMyBets([]);
    if (selectedPred !== id) loadMyBets(id);
  };

  const handleBet = async (predId, optionIndex) => {
    if (!isConnected) return toast.error(t('connectWalletFirst'));
    const usdVal = betAmounts[`${predId}-${optionIndex}`];
    if (!usdVal || parseFloat(usdVal) <= 0) {
      return toast.error(t('enterBetAmount'));
    }
    setPlacing(`${predId}-${optionIndex}`);
    try {
      const res = await api.post(`/predictions/${predId}/bet`, {
        optionIndex,
        amountWei: usdToWei(usdVal)
      });
      toast.success(res.data?.message || t('betPlaced'));
      setBetAmounts(prev => ({ ...prev, [`${predId}-${optionIndex}`]: '' }));
      fetchPredictions();
      if (selectedPred === predId) loadMyBets(predId);
    } catch (e) {
      toast.error(e?.response?.data?.message || t('betFailed'));
    } finally {
      setPlacing(null);
    }
  };

  const now = new Date();

  if (loading) return <div className="pred-loading">{t('loading')}</div>;

  return (
    <div className="prediction-section">
      {predictions.length === 0 && <div className="pred-empty">{t('noActivePredictions')}</div>}

      {predictions.map(p => {
        const isActive = p.status === 'active' && new Date(p.startsAt) <= now && new Date(p.endsAt) >= now;
        const isSelected = selectedPred === p._id;
        const totalPool = BigInt(p.totalPoolWei || '0');

        return (
          <div key={p._id} className={`pred-card ${isSelected ? 'pred-card-expanded' : ''}`}>
            <div className="pred-card-top" onClick={() => handleSelect(p)}>
              <div className="pred-card-info">
                <h3>{p.question}</h3>
                {p.description && <p className="pred-desc">{p.description}</p>}
              </div>
              <span className={`pred-status pred-status-${p.status}`}>{p.status}</span>
            </div>

            <div className="pred-meta">
              <span>{t('pool')}: <strong className="pred-gold">${weiToUsd(p.totalPoolWei)} USDT</strong></span>
              <span>{t('endsAt')}: {new Date(p.endsAt).toLocaleDateString()}</span>
              <span>{t('platformFee')}: {p.platformFeePercent}%</span>
            </div>

            <div className="pred-options">
              {p.options?.map((o, i) => {
                const optPool = BigInt(o.totalBetsWei || '0');
                const pct = totalPool > 0n ? Number((optPool * 100n) / totalPool) : 0;
                const isWinner = p.resolvedOptionIndex === i;
                const key = `${p._id}-${i}`;

                return (
                  <div key={i} className={`pred-option ${isWinner ? 'pred-option-winner' : ''}`}>
                    <div className="pred-option-bar" style={{ width: `${Math.max(pct, 2)}%` }} />
                    <div className="pred-option-content">
                      <div className="pred-option-left">
                        <span className="pred-option-label">{o.label}</span>
                        <span className="pred-option-pct">{pct}%</span>
                      </div>
                      <span className="pred-option-pool">${weiToUsd(o.totalBetsWei)}</span>
                      {isWinner && <span className="pred-winner-tag">{t('winner')}</span>}
                    </div>
                    {isActive && (
                      <div className="pred-bet-row">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={`$ USDT`}
                          value={betAmounts[key] || ''}
                          onChange={e => setBetAmounts(prev => ({ ...prev, [key]: e.target.value }))}
                          className="pred-bet-input"
                          onClick={e => e.stopPropagation()}
                        />
                        <button
                          className="pred-bet-btn"
                          onClick={(e) => { e.stopPropagation(); handleBet(p._id, i); }}
                          disabled={placing === key}
                        >
                          {placing === key ? '...' : t('bet')}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {isSelected && myBets.length > 0 && (
              <div className="pred-my-bets">
                <h4>{t('myBets')}</h4>
                {myBets.map((b, i) => (
                  <div key={i} className="pred-bet-entry">
                    <span>{p.options?.[b.optionIndex]?.label || `#${b.optionIndex}`}</span>
                    <span>${weiToUsd(b.amountWei)}</span>
                    {b.paidOut && <span className={b.payoutWei !== '0' ? 'pred-win' : 'pred-loss'}>
                      {b.payoutWei !== '0' ? `+$${weiToUsd(b.payoutWei)}` : t('lost')}
                    </span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PredictionSection;
